import { Card, Chip, Stack, Typography } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import { PageContainer, PageHeader } from "../components/PageHeader";
import VodTitlesPage from "./VodTitlesPage";
import CarouselsPage from "./CarouselsPage";
import ChannelsPage from "./ChannelsPage";
import ChannelLineupPage from "./ChannelLineupPage";
import ContentPackagesPage from "./ContentPackagesPage";
import ContentProvidersPage from "./ContentProvidersPage";
import ContentProductsPage from "./ContentProductsPage";
import SubmissionsPage from "./SubmissionsPage";
import BulkIngestPage from "./BulkIngestPage";

/**
 * Distribution - the platform-level content pipeline.
 *
 * Everything under a product area answers "what does THIS place show?".
 * Distribution answers the question one level up: what is there to show at all,
 * and how does it get onto the platform. Channels are the broadcast line-up,
 * VOD is the on-demand shelf, and bulk ingest is the pipe that fills both.
 *
 * None of it is scoped to a location, so it lives in the Admin block beside the
 * other Nenda-side catalogues rather than inside a product.
 *
 * Channels and VOD are real surfaces over the Nenda Content API (`BE-15`), and
 * bulk ingest is real too since `BE-31` — a package of many titles over the
 * single-title lifecycle `BE-17` shipped.
 *
 * The placeholder below is kept because the shape it holds is still the right
 * one for the next section that exists in the design and not yet in the app.
 */

interface DistributionSection {
  key: string;
  title: string;
  summary: string;
  /** What this page will do once it is built - written now, while it is fresh. */
  intent: string[];
}

const SECTIONS: DistributionSection[] = [
  {
    key: "channels",
    title: "Channels",
    summary:
      "Every live channel the platform carries, read from the Nenda content catalogue.",
    intent: [],
  },
  {
    key: "lineup",
    title: "Line-up",
    summary:
      "The order channels appear in, everywhere. An order, not a selection — a hotel can depart from it.",
    intent: [],
  },
  {
    key: "vod",
    title: "VOD",
    summary:
      "The on-demand shelf: titles, artwork, rights windows and availability.",
    intent: [
      "Titles with their artwork, metadata and playable renditions.",
      "Rights windows - when a title may be offered, and to which places.",
      "Curation into the rows a guest actually sees on the TV.",
    ],
  },
  {
    key: "carousels",
    title: "Carousels",
    summary:
      "The rows a guest sees on the home page. Authored once, globally; selected and ordered per hotel.",
    intent: [],
  },
  {
    key: "products",
    title: "Products",
    summary:
      "What a customer is sold. A product carries a price and grants one or more packages.",
    intent: [],
  },
  {
    key: "packages",
    title: "Packages",
    summary:
      "The bag of content a product grants: titles curated by hand, or matched by a rule over the content providers Nenda has licensed.",
    intent: [],
  },
  {
    key: "providers",
    title: "Content providers",
    summary:
      "Who Nenda has licensed content from, and which of them a package rule may carry. Trusting one is a separate permission from seeing them.",
    intent: [],
  },
  {
    key: "submissions",
    title: "Review queue",
    summary:
      "Supplier uploads waiting to be screened. Nothing here is in a package yet, so nothing here can be seen or played.",
    intent: [],
  },
  {
    key: "bulk-ingest",
    title: "Bulk ingest",
    summary:
      "The pipe that fills the shelves: submit a package, watch it validate, transcode and land.",
    intent: [],
  },
];

/**
 * Sections that used to exist and now redirect.
 *
 * "Effect channels" and "Live channels" were two menu entries. Live channels
 * became the Channels list; EFFECT channels went away entirely - an effect
 * channel is a loop you build, and Foyer already has a first-class model for
 * that in `SignagePlaylist` (`BE-3`, superseded). Both names were in the menu,
 * so both are in somebody's bookmarks - a 404 for them would be a
 * self-inflicted one, and landing on Channels is the closest true answer.
 */
const MERGED_SECTIONS: Record<string, string> = {
  "effect-channels": "channels",
  "live-channels": "channels",
};

export default function DistributionPage() {
  const { section } = useParams<{ section: string }>();
  const merged = section ? MERGED_SECTIONS[section] : undefined;
  const match = SECTIONS.find((s) => s.key === section);

  if (merged) return <Navigate to={`/distribution/${merged}`} replace />;
  // An unknown section is a stale bookmark, not an error worth a page.
  if (!match) return <Navigate to="/distribution/channels" replace />;

  // The sections with a real surface behind them. The rest fall through to the
  // placeholder below.
  if (match.key === "channels") return <ChannelsPage />;
  if (match.key === "lineup") return <ChannelLineupPage />;
  if (match.key === "vod") return <VodTitlesPage />;
  if (match.key === "carousels") return <CarouselsPage />;
  if (match.key === "products") return <ContentProductsPage />;
  if (match.key === "packages") return <ContentPackagesPage />;
  if (match.key === "providers") return <ContentProvidersPage />;
  if (match.key === "submissions") return <SubmissionsPage />;
  if (match.key === "bulk-ingest") return <BulkIngestPage />;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Distribution"
        title={match.title}
        subtitle={match.summary}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Not built yet"
          />
          <Typography variant="body2" color="text.secondary">
            The section is here because the area is real - it just has no editor
            behind it yet. What it is meant to hold:
          </Typography>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {match.intent.map((line) => (
              <Typography
                key={line}
                component="li"
                variant="body2"
                color="text.secondary"
              >
                {line}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Card>
    </PageContainer>
  );
}
